import { useSyncExternalStore } from "react";

export type UnityRuntimeReadyState =
  | "booting"
  | "bridge_ready"
  | "handlers_ready"
  | "scene_ready";

export const UNITY_RUNTIME_READY_STATE_EVENT = "unity:runtimeReadyState";

const READY_LEVEL: Record<UnityRuntimeReadyState, number> = {
  booting: 0,
  bridge_ready: 1,
  handlers_ready: 2,
  scene_ready: 3,
};

export type UnityRuntimeBridgeSnapshot = {
  readyState: UnityRuntimeReadyState;
  transactionId: string | null;
  queueDepth: number;
  readyBoundarySeq: number;
  updatedAt: number;
  source: string | null;
};

export type SetUnityRuntimeReadyStateArgs = {
  readyState: UnityRuntimeReadyState;
  transactionId?: string | null;
  source?: string | null;
  force?: boolean;
};

let runtimeBridgeSnapshot: UnityRuntimeBridgeSnapshot = {
  readyState: "booting",
  transactionId: null,
  queueDepth: 0,
  readyBoundarySeq: 0,
  updatedAt: Date.now(),
  source: "init",
};
const runtimeBridgeListeners = new Set<() => void>();

function emitRuntimeStateEvent(): void {
  runtimeBridgeListeners.forEach((listener) => listener());
  window.dispatchEvent(
    new CustomEvent(UNITY_RUNTIME_READY_STATE_EVENT, {
      detail: { ...runtimeBridgeSnapshot },
    }),
  );
}

export function getUnityRuntimeBridgeSnapshot(): UnityRuntimeBridgeSnapshot {
  return { ...runtimeBridgeSnapshot };
}

export function isUnityRuntimeReadyAtLeast(
  requiredState: UnityRuntimeReadyState,
): boolean {
  const current = READY_LEVEL[runtimeBridgeSnapshot.readyState];
  const required = READY_LEVEL[requiredState];
  return current >= required;
}

export function subscribeUnityRuntimeBridgeState(
  listener: () => void,
): () => void {
  runtimeBridgeListeners.add(listener);
  return () => runtimeBridgeListeners.delete(listener);
}

export function useUnityRuntimeReadyAtLeast(
  requiredState: UnityRuntimeReadyState,
): boolean {
  return useSyncExternalStore(
    subscribeUnityRuntimeBridgeState,
    () => isUnityRuntimeReadyAtLeast(requiredState),
    () => false,
  );
}

export function setUnityRuntimeReadyState(
  args: SetUnityRuntimeReadyStateArgs,
): UnityRuntimeBridgeSnapshot {
  const previous = runtimeBridgeSnapshot;
  const previousLevel = READY_LEVEL[previous.readyState];
  const nextLevel = READY_LEVEL[args.readyState];
  const incomingTransaction =
    args.transactionId === undefined ? previous.transactionId : args.transactionId;
  // transaction이 바뀌면 새 lifecycle이므로 readiness를 되돌린다. 다만 그 판정은
  // **실제 transaction 값이 있을 때만** 유효하다.
  //
  // Import 완료 경로(`operation=project_reload`)의 navigation 이벤트는 correlation
  // transaction 없이 도착한다(Unity 로그: `vse_lifecycle ...: transactionId=`).
  // 그 빈 값을 "transaction이 바뀌었다"로 읽으면 `scene_ready`가 낮은 단계로 강등되고,
  // 같은 `scene_ready` 조건을 쓰는 세 곳이 한꺼번에 잠긴다:
  // 자동 저장(beginProjectSaveUiOperation), 상단 Save, 저장 확인 팝업의 Save.
  // 그러면 사용자는 저장할 수단이 없고 재오픈 시 Import 결과가 사라진다.
  // (사용자 확인 2026-09-22 · STG build 1790037047334 · `Untitled (21)`)
  const hasIncomingTransaction =
    typeof incomingTransaction === "string" && incomingTransaction.trim() !== "";
  const transactionChanged =
    hasIncomingTransaction && incomingTransaction !== previous.transactionId;
  const shouldResetToBooting = args.readyState === "booting" || transactionChanged;
  const shouldPromote =
    args.force === true ||
    shouldResetToBooting ||
    nextLevel > previousLevel;

  if (!shouldPromote) {
    return { ...runtimeBridgeSnapshot };
  }

  runtimeBridgeSnapshot = {
    ...previous,
    readyState: args.readyState,
    // correlation 없는 이벤트가 기존 transaction 표식을 지우지 않게 한다.
    // 다만 명시적 booting reset은 lifecycle 종료이므로 표식도 비운다.
    transactionId: hasIncomingTransaction
      ? incomingTransaction
      : args.readyState === "booting" && args.transactionId !== undefined
        ? null
        : previous.transactionId,
    readyBoundarySeq:
      shouldResetToBooting || args.readyState !== previous.readyState
        ? previous.readyBoundarySeq + 1
        : previous.readyBoundarySeq,
    updatedAt: Date.now(),
    source: args.source ?? null,
  };

  emitRuntimeStateEvent();
  return { ...runtimeBridgeSnapshot };
}

export function setUnityRuntimeQueueDepth(
  queueDepth: number,
  source?: string,
): UnityRuntimeBridgeSnapshot {
  const nextDepth =
    typeof queueDepth === "number" && Number.isFinite(queueDepth)
      ? Math.max(0, Math.trunc(queueDepth))
      : 0;
  if (
    runtimeBridgeSnapshot.queueDepth === nextDepth &&
    (source === undefined || source === runtimeBridgeSnapshot.source)
  ) {
    return { ...runtimeBridgeSnapshot };
  }

  runtimeBridgeSnapshot = {
    ...runtimeBridgeSnapshot,
    queueDepth: nextDepth,
    updatedAt: Date.now(),
    source: source ?? runtimeBridgeSnapshot.source,
  };

  emitRuntimeStateEvent();
  return { ...runtimeBridgeSnapshot };
}
